import { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import useSWR from "swr";
import { visionService } from "@/services/vision-service";
import {
  ArrowLeft,
  Briefcase,
  Building2,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Clock,
  Copy,
  DollarSign,
  ExternalLink,
  Hash,
  Link2,
  MapPin,
  Maximize2,
  Pencil,
  Sparkles,
  Users,
} from "lucide-react";
import { EllipsisVerticalIcon } from "@heroicons/react/24/outline";
import { ProposalListItem } from "@/services/vision-service/types";
import type { RankedSpace } from "@/features/vision-360/ai-mode/types";
import {
  groupRoomsIntoCombos,
  sumComboTotals,
} from "@/features/vision-360/ai-mode/utils/comboGrouping";
import { SpaceDetailDrawer } from "@/features/vision-360/ai-mode/components/SpaceDetailDrawer";
import { Button } from "@/components/ui/button";
import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarTrigger,
} from "@/components/ui/menubar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { DEFAULT_ROUTES } from "@/constants/routes";
import { formatCurrency } from "@/utils/format/currency";
import { formatDate } from "@/utils/format/format-date";
import { copyToClipboard } from "@/utils/copy-to-clipboard";
import { getProposalUrl } from "@/features/vision-360/utils/proposal-url";
import {
  formatProposalDate,
  getAverageMonthlyValue,
  formatAverageMonthlyValue,
} from "../list/utils";

type LocationState = {
  proposal: ProposalListItem;
};

function InfoItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="text-xs text-muted-foreground">{label}</span>
      <p className="text-sm font-medium mt-0.5">{value}</p>
    </div>
  );
}

function ValueRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-right">{value}</span>
    </div>
  );
}

function toRankedSpace(
  space: NonNullable<ProposalListItem["selected_spaces"]>[number],
): RankedSpace {
  return {
    id: space.id,
    spaceName: space.spaceName ?? "",
    roomName: space.roomName ?? "",
    city: space.city ?? "",
    address: space.address ?? "",
    lat: space.lat ?? 0,
    lng: space.lng ?? 0,
    pricePerWorkstation: space.pricePerWorkstation ?? 0,
    monthlyPrice: space.monthlyPrice ?? null,
    credits: null,
    capacity: space.capacity ?? 0,
    sizeM2: space.sizeM2 ?? null,
    score: space.score ?? 0,
    amenities: space.amenities ?? [],
    spaceAmenities: space.spaceAmenities ?? [],
    imageUrl: space.imageUrl ?? "",
    // Show the curated subset (room + space) when present, falling back to all.
    images: space.selectedImages ?? space.images ?? [],
    spacePhotos: space.selectedSpacePhotos ?? space.spacePhotos ?? [],
    partnerId: space.partnerId ?? undefined,
    searchId: space.searchId ?? undefined,
    groupIds: space.groupIds ?? undefined,
    isIndividual: space.isIndividual ?? undefined,
  };
}

export function ProposalDetailPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { uuid } = useParams<{ uuid: string }>();
  const state = location.state as LocationState | null;
  const stateProposal = state?.proposal;
  const [drawerSpace, setDrawerSpace] = useState<RankedSpace | null>(null);
  const [narrativeExpanded, setNarrativeExpanded] = useState(false);
  const [narrativeClamped, setNarrativeClamped] = useState(false);
  const narrativeRef = useRef<HTMLParagraphElement>(null);

  // Fallback: fetch by UUID when navigation state is missing (refresh / direct URL)
  const {
    data: fetchedProposal,
    isLoading,
    error: fetchError,
  } = useSWR(
    !stateProposal && uuid ? ["/api/v1/proposals", uuid] : null,
    ([, id]) => visionService.getProposalListItem(id),
  );

  const proposal = stateProposal ?? fetchedProposal;

  useEffect(() => {
    const el = narrativeRef.current;
    if (el) setNarrativeClamped(el.scrollHeight > el.clientHeight);
  }, [proposal?.summary_narrative]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center">
        <p className="text-sm text-muted-foreground">Carregando proposta...</p>
      </div>
    );
  }

  if (!proposal) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center">
        <div className="text-center space-y-3">
          <p className="text-sm text-muted-foreground">
            {fetchError
              ? "Erro ao carregar a proposta."
              : "Proposta não encontrada."}
          </p>
          <button
            type="button"
            onClick={() => navigate(DEFAULT_ROUTES.VISION_360_PROPOSALS)}
            className="text-sm text-foreground underline"
          >
            Voltar à listagem
          </button>
        </div>
      </div>
    );
  }

  const averageMonthlyValue = getAverageMonthlyValue(proposal);

  const averagePerPosition: number | null = (() => {
    const spaces = proposal.selected_spaces ?? [];
    const adjustments = proposal.space_adjustments ?? [];
    const totalPositions = spaces.reduce(
      (sum, s) => sum + (s.capacity ?? 0),
      0,
    );
    const totalPrice = adjustments.reduce(
      (sum, a) => sum + (a.total_price ?? 0),
      0,
    );
    if (totalPositions === 0 || totalPrice === 0) return null;
    return totalPrice / totalPositions;
  })();

  const capacityLabel =
    proposal.hard_constraints?.min_capacity != null &&
    proposal.hard_constraints?.max_capacity != null
      ? `${proposal.hard_constraints.min_capacity}–${proposal.hard_constraints.max_capacity} posições`
      : null;

  return (
    <div className="min-h-screen bg-stone-100 px-8 py-6">
      <div className="mx-auto max-w-[80rem] space-y-6">
        {/* Back + Actions */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <button
            type="button"
            onClick={() => navigate(DEFAULT_ROUTES.VISION_360_PROPOSALS)}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar à listagem
          </button>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() =>
                window.open(
                  `${DEFAULT_ROUTES.VISION_360_AI_MODE}?edit=${proposal.uuid}`,
                  "_blank",
                )
              }
              className="inline-flex h-10 items-center gap-1.5 rounded-md border bg-stone-100 px-3 text-sm font-medium shadow-sm hover:bg-white transition-colors"
            >
              <Pencil className="h-4 w-4" />
              Editar proposta
            </button>
            <Button
              onClick={() => copyToClipboard(getProposalUrl(proposal.uuid))}
              className="h-10 bg-[var(--woba-yellow)] hover:bg-[var(--woba-yellow-hover)] text-black font-medium shadow-none border-none"
            >
              <Link2 className="mr-2 h-4 w-4" />
              Copiar link da proposta
            </Button>
            <Menubar className="border-none shadow-none p-0">
              <MenubarMenu>
                <MenubarTrigger className="cursor-pointer h-10 px-2 rounded-md border bg-stone-100 shadow-sm hover:bg-white transition-colors">
                  <EllipsisVerticalIcon width={20} />
                </MenubarTrigger>
                <MenubarContent align="end">
                  <MenubarItem
                    onClick={() =>
                      window.open(getProposalUrl(proposal.uuid), "_blank")
                    }
                  >
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Abrir proposta enviada
                  </MenubarItem>
                </MenubarContent>
              </MenubarMenu>
            </Menubar>
          </div>
        </div>

        {/* Header card */}
        <section className="bg-white rounded-2xl border p-6 space-y-5">
          <div className="flex items-center gap-2">
            <Briefcase className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-base font-bold">Informações do cliente</h2>
          </div>

          {/* Top row: empresa + Deal ID */}
          <div className="grid grid-cols-1 gap-x-4 gap-y-4 items-start md:grid-cols-3">
            <div className="min-w-0">
              <span className="text-xs text-muted-foreground">
                Nome da empresa
              </span>
              <div className="mt-0.5 flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-bold tracking-[-0.03em]">
                  {proposal.company_name ?? "—"}
                </h1>
              </div>
            </div>
            {proposal.deal_context?.deal_id && (
              <div>
                <span className="text-xs text-muted-foreground">
                  Deal ID HubSpot
                </span>
                <div className="mt-0.5 flex items-center gap-1.5">
                  <span className="inline-flex items-center gap-1 text-sm font-medium">
                    <Hash className="h-3.5 w-3.5" />
                    {proposal.deal_context.deal_id}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      copyToClipboard(proposal.deal_context!.deal_id!)
                    }
                    aria-label="Copiar Deal ID"
                    className="inline-flex text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Details grid */}
          <div className="border-t pt-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-4 items-start">
              <div className="space-y-4">
                <InfoItem
                  label="Segmento"
                  value={proposal.deal_context?.company_industry ?? "—"}
                />
                <InfoItem
                  label="Nome do contato"
                  value={proposal.deal_context?.contact_name ?? "—"}
                />
                {capacityLabel && (
                  <InfoItem
                    label="Capacidade solicitada"
                    value={capacityLabel}
                  />
                )}
              </div>
              <div className="space-y-4">
                <InfoItem
                  label="Localização"
                  value={
                    proposal.hard_constraints?.city?.split(" -")[0] ??
                    proposal.location_label ??
                    "—"
                  }
                />
                <InfoItem
                  label="Email do contato"
                  value={proposal.deal_context?.contact_email ?? "—"}
                />
                <InfoItem
                  label="Custo mensal atual"
                  value={
                    proposal.current_monthly_cost != null
                      ? formatCurrency(proposal.current_monthly_cost)
                      : "—"
                  }
                />
              </div>
              {proposal.summary_narrative && (
                <div className="min-w-0">
                  <span className="text-xs text-muted-foreground">
                    Necessidades específicas
                  </span>
                  <p
                    ref={narrativeRef}
                    className={`text-sm mt-1 leading-[1.7] ${!narrativeExpanded ? "line-clamp-5" : ""}`}
                  >
                    {proposal.summary_narrative}
                  </p>
                  {(narrativeClamped || narrativeExpanded) && (
                    <button
                      type="button"
                      onClick={() => setNarrativeExpanded((v) => !v)}
                      className="mt-1 inline-flex items-center gap-0.5 text-sm text-muted-foreground hover:text-foreground"
                    >
                      {narrativeExpanded ? (
                        <>
                          mostrar menos <ChevronUp className="h-3.5 w-3.5" />
                        </>
                      ) : (
                        <>
                          leia mais <ChevronDown className="h-3.5 w-3.5" />
                        </>
                      )}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Main grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          {/* Spaces card */}
          <section className="bg-white rounded-2xl border p-6 space-y-4 lg:col-span-2">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <h2 className="text-base font-bold">Salas selecionadas</h2>
              {proposal.selected_spaces && (
                <span className="text-xs text-muted-foreground ml-auto">
                  {proposal.selected_spaces.length}{" "}
                  {proposal.selected_spaces.length === 1 ? "Sala" : "Salas"}
                </span>
              )}
            </div>

            {!proposal.selected_spaces ||
            proposal.selected_spaces.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Nenhuma sala selecionada.
              </p>
            ) : (
              <div className="space-y-3">
                {(() => {
                  const renderRoomCard = (
                    space: NonNullable<
                      ProposalListItem["selected_spaces"]
                    >[number],
                  ) => {
                    const adj = (proposal.space_adjustments ?? []).find(
                      (a) => a.space_id === space.id,
                    );
                    return (
                      <div
                        key={space.id}
                        className="rounded-xl border bg-stone-100 p-5 hover:shadow-sm transition-shadow"
                      >
                        <div className="flex flex-col gap-5 md:flex-row md:items-stretch md:justify-between">
                          {/* Left */}
                          <div className="min-w-0 flex-1 space-y-4">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="text-base font-bold">
                                {space.spaceName ?? "—"}
                              </p>
                              {space.isRecommended && (
                                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#f9e10d26] px-2.5 py-1 text-[11px] font-semibold">
                                  <Sparkles className="h-3.5 w-3.5 text-black" />
                                  Espaço recomendado
                                </span>
                              )}
                            </div>
                            {space.roomName && (
                              <p className="text-sm text-muted-foreground font-medium">
                                {space.roomName}
                              </p>
                            )}
                            {(space.city || space.address) && (
                              <div className="flex items-start gap-1 text-sm text-muted-foreground">
                                <MapPin className="h-4 w-4 shrink-0 mt-0.5" />
                                <span>
                                  {[space.address, space.city]
                                    .filter(Boolean)
                                    .join(", ")}
                                </span>
                              </div>
                            )}
                            <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-muted-foreground pt-1">
                              {space.capacity != null && (
                                <span className="inline-flex items-center gap-1.5">
                                  <Users className="h-4 w-4" />
                                  {space.capacity} posições
                                </span>
                              )}
                              {space.sizeM2 != null && (
                                <span className="inline-flex items-center gap-1.5">
                                  <Maximize2 className="h-4 w-4" />
                                  {space.sizeM2} m²
                                </span>
                              )}
                            </div>
                            {space.availabilityDate && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className="flex items-center gap-1.5 text-sm text-muted-foreground cursor-default w-fit">
                                      <Clock className="h-4 w-4 shrink-0" />
                                      <span>
                                        Data de disponibilidade:{" "}
                                        {formatDate(space.availabilityDate)}
                                      </span>
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-64 text-xs bg-gray-800 text-white">
                                    Data de disponibilidade prevista no momento
                                    da criação da proposta
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>

                          {/* Right: financials */}
                          {adj && (
                            <div className="md:w-[280px] md:shrink-0 rounded-lg border bg-muted/30 p-4 space-y-2 text-left">
                              {space.monthlyPrice != null && (
                                <div className="flex items-center justify-between gap-3 text-xs">
                                  <span className="text-muted-foreground font-light">
                                    Valor mensal
                                  </span>
                                  <span className="font-light">
                                    {formatCurrency(space.monthlyPrice)}
                                  </span>
                                </div>
                              )}
                              {adj.extra_value != null && (
                                <div className="flex items-center justify-between gap-3 text-xs">
                                  <span className="text-muted-foreground font-light">
                                    Valor extra
                                  </span>
                                  <span className="font-light">
                                    {formatCurrency(adj.extra_value)}
                                  </span>
                                </div>
                              )}
                              {adj.margin != null && (
                                <div className="flex items-center justify-between gap-3 text-xs">
                                  <span className="text-muted-foreground font-light">
                                    Margem alvo
                                  </span>
                                  <span className="font-light">
                                    {adj.margin}%
                                  </span>
                                </div>
                              )}
                              <div className="border-t pt-3 space-y-1">
                                <span className="text-[11px] font-semibold uppercase tracking-[0.04em] text-muted-foreground">
                                  Total
                                </span>
                                <div className="flex items-end justify-between gap-3">
                                  {adj.total_credits != null && (
                                    <span className="text-[11px] font-bold text-foreground">
                                      {adj.total_credits} créditos
                                      {adj.extra_credits != null &&
                                        adj.extra_credits > 0 && (
                                          <span className="font-bold text-foreground">
                                            {" "}
                                            +{adj.extra_credits} extras
                                          </span>
                                        )}
                                    </span>
                                  )}
                                  {adj.client_price != null && (
                                    <span className="text-lg font-bold leading-tight ml-auto">
                                      {formatCurrency(adj.client_price)}
                                    </span>
                                  )}
                                </div>
                                {adj.client_price != null &&
                                  space.capacity != null &&
                                  space.capacity > 0 && (
                                    <p className="text-[11px] text-muted-foreground text-right">
                                      {formatCurrency(
                                        adj.client_price / space.capacity,
                                      )}
                                      /posição
                                    </p>
                                  )}
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="mt-3 pt-3 border-t flex justify-end">
                          <button
                            type="button"
                            onClick={() => setDrawerSpace(toRankedSpace(space))}
                            className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-0.5 transition-colors"
                          >
                            Ver detalhes
                            <ChevronRight className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    );
                  };
                  const adjById = new Map(
                    (proposal.space_adjustments ?? []).map((a) => [
                      a.space_id,
                      a,
                    ]),
                  );
                  // Group rooms: standalone (individual) rooms render as cards;
                  // rooms of a selected combo render inside a "Conjunto" card. A
                  // room that is both shows in both.
                  const units = groupRoomsIntoCombos(
                    (proposal.selected_spaces ?? []).map((s) => ({
                      ...s,
                      groupIds: s.groupIds ?? undefined,
                      isIndividual: s.isIndividual ?? undefined,
                    })),
                  );
                  return units.map((unit) => {
                    if (unit.kind === "single")
                      return renderRoomCard(unit.space);
                    const { totalCapacity, totalPrice: totalClient } =
                      sumComboTotals(
                        unit.spaces,
                        (s) => adjById.get(s.id)?.client_price ?? 0,
                      );
                    return (
                      <div
                        key={`combo-${unit.comboId}`}
                        className="rounded-2xl border-2 p-3 space-y-3"
                        style={{
                          borderColor: "#f9e10d",
                          background: "#f9e10d0d",
                        }}
                      >
                        <p className="flex items-center gap-1.5 px-1 text-sm font-bold text-[#1A1814]">
                          <Link2 className="h-4 w-4" />
                          Conjunto · {totalCapacity} posições ·{" "}
                          {formatCurrency(totalClient)}/mês
                        </p>
                        {unit.spaces.map(renderRoomCard)}
                      </div>
                    );
                  });
                })()}
              </div>
            )}
          </section>

          {/* Right column */}
          <div className="lg:col-span-1 flex flex-col gap-6">
            {/* Informações da oferta */}
            <section className="bg-white rounded-2xl border p-5 space-y-4">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <h2 className="text-base font-bold">Informações da oferta</h2>
              </div>
              <div className="space-y-1 text-xs text-muted-foreground">
                <p className="flex items-center gap-1.5">
                  <strong className="font-semibold text-foreground">
                    Gerada em:
                  </strong>{" "}
                  {formatProposalDate(
                    proposal.updated_at ?? proposal.createdAt,
                  )}
                  <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold bg-muted text-muted-foreground">
                    V{proposal.version_number ?? 1}
                  </span>
                </p>
                {proposal.expiresAt && (
                  <p>
                    <strong className="font-semibold text-foreground">
                      Válida até:
                    </strong>{" "}
                    {formatProposalDate(proposal.expiresAt)}
                  </p>
                )}
                <p className="flex items-center gap-1">
                  <strong className="font-semibold text-foreground">
                    ID da proposta:
                  </strong>{" "}
                  <span title={proposal.uuid}>
                    {proposal.uuid.slice(0, 8)}…{proposal.uuid.slice(-4)}
                  </span>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(proposal.uuid)}
                    aria-label="Copiar ID da proposta"
                    className="inline-flex align-middle text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Copy className="h-3 w-3" />
                  </button>
                </p>
              </div>
              <div className="pt-3 border-t">
                <span className="text-xs text-muted-foreground">
                  Executivo comercial
                </span>
                <p className="mt-1.5 text-sm font-medium">
                  {proposal.created_by?.full_name ?? "—"}
                </p>
              </div>
            </section>

            {/* Values card */}
            <section className="bg-white rounded-2xl border p-6 space-y-4">
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-muted-foreground" />
                <h2 className="text-base font-bold">Valores da proposta</h2>
              </div>

              <div className="space-y-3">
                <div className="rounded-lg border p-4">
                  <span className="text-xs text-muted-foreground">
                    Valor médio mensal
                  </span>
                  <p className="mt-1 text-xl font-bold">
                    {formatAverageMonthlyValue(averageMonthlyValue)}
                  </p>
                  {averagePerPosition != null && (
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {formatCurrency(averagePerPosition)}/posição
                    </p>
                  )}
                </div>

                <div className="rounded-lg border p-4">
                  <span className="text-xs text-muted-foreground">
                    Prazo de contrato
                  </span>
                  <p className="mt-1 text-xl font-bold">
                    {proposal.contract_months != null
                      ? `${proposal.contract_months} meses`
                      : "—"}
                  </p>
                </div>

                {proposal.show_license_fee === true && (
                  <div className="rounded-lg border p-4 space-y-3">
                    <span className="text-[10px] font-bold tracking-[0.08em] uppercase text-muted-foreground">
                      License fee
                    </span>
                    <div className="space-y-2">
                      {proposal.license_fee_percentage != null && (
                        <ValueRow
                          label="Percentual"
                          value={`${proposal.license_fee_percentage}%`}
                        />
                      )}
                      {proposal.license_fee_value != null && (
                        <ValueRow
                          label="Valor estabelecido"
                          value={formatCurrency(proposal.license_fee_value)}
                        />
                      )}
                    </div>
                  </div>
                )}

                {proposal.include_setup_fee === true && (
                  <div className="rounded-lg border p-4 space-y-3">
                    <span className="text-[10px] font-bold tracking-[0.08em] uppercase text-muted-foreground">
                      Setup fee
                    </span>
                    <div className="space-y-2">
                      {proposal.setup_fee != null && (
                        <ValueRow
                          label="Valor (pago uma única vez)"
                          value={formatCurrency(proposal.setup_fee)}
                        />
                      )}
                    </div>
                  </div>
                )}

                {proposal.include_flex_credits === true && (
                  <div className="rounded-lg border p-4 space-y-3">
                    <span className="text-[10px] font-bold tracking-[0.08em] uppercase text-muted-foreground">
                      Créditos flexíveis
                    </span>
                    <div className="space-y-2">
                      {proposal.credits_price != null && (
                        <ValueRow
                          label="Valor do crédito"
                          value={formatCurrency(proposal.credits_price)}
                        />
                      )}
                      {proposal.flex_credits != null && (
                        <ValueRow
                          label="Quantidade"
                          value={`${proposal.flex_credits} créditos/mês`}
                        />
                      )}
                      {proposal.flex_credits != null &&
                        proposal.credits_price != null && (
                          <ValueRow
                            label="Valor total"
                            value={`${formatCurrency(proposal.flex_credits * proposal.credits_price)}/mês`}
                          />
                        )}
                    </div>
                  </div>
                )}

                {proposal.seller_notes && (
                  <div className="rounded-lg p-4 border">
                    <span className="text-xs text-muted-foreground">
                      Observações do vendedor
                    </span>
                    <p className="mt-1 text-sm leading-relaxed">
                      {proposal.seller_notes}
                    </p>
                  </div>
                )}
              </div>
            </section>
          </div>
        </div>
      </div>

      <SpaceDetailDrawer
        open={drawerSpace !== null}
        space={drawerSpace}
        onClose={() => setDrawerSpace(null)}
        hidePrice
      />
    </div>
  );
}
